import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import GradientCircularProgress from '../../components/common/GradientCircularProgress';
import { useThemeContext } from '../../contexts/BaseThemeContextProvider';
import { OrganizationType } from '../../types/organization';
import useBackendApi from '../../hooks/useBackendApi';
import { RequestOptions } from '../../types/httpRequests';
import PrimaryBlueButton from '../../components/common/buttons/PrimaryBlueButton';
import BorderedButton from '../../components/common/buttons/BorderedButton';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

export default function EditOrganization(): JSX.Element {
  const { theme } = useThemeContext();
  const { org_path_name } = useParams<{ org_path_name: string }>();
  const navigate = useNavigate();
  const { apiFetchRequest, isLoading } = useBackendApi();

  const [org, setOrg] = useState<OrganizationType | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [saveErrorText, setSaveErrorText] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Form fields
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [googleCalendarId, setGoogleCalendarId] = useState<string>('');

  const fail = useCallback(() => setErrorText('Something went wrong loading organization details. Please try again.'), []);

  useEffect(() => {
    if (!org_path_name) {
      navigate('/404.html');
      return;
    }

    const fetchOrg = async () => {
      const opts: RequestOptions = {
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}`,
        method: 'GET',
      };

      try {
        const res = await apiFetchRequest(opts);

        if (!res || res.status !== 200) {
          navigate('/404.html');
          return;
        }

        const body = res.body;

        if (body?.message === 'NO_ACCESS' || body.status_code === 401) {
          navigate('/401.html');
          return;
        }
        if (body?.message !== 'SUCCESS' || !body?.organization) {
          fail();
          return;
        }

        const organization = body.organization as OrganizationType;
        setOrg(organization);
        setName(organization.name || '');
        setDescription(organization.description || '');
        setGoogleCalendarId(organization.google_calendar_id || '');
        setErrorText(null);
      } catch {
        fail();
      }
    };

    fetchOrg();
  }, [apiFetchRequest, fail, navigate, org_path_name]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!org_path_name || !org) return;

    if (!name.trim()) {
      setSaveErrorText('Organization name is required.');
      return;
    }

    setIsSaving(true);
    setSaveErrorText(null);

    const opts: RequestOptions = {
      endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}`,
      method: 'PUT',
      body: {
        name: name.trim(),
        description: description.trim() || null,
        google_calendar_id: googleCalendarId.trim() || null,
      },
    };

    try {
      const res = await apiFetchRequest(opts);
      if (!res) {
        setSaveErrorText('Failed to save changes. Please try again.');
        return;
      }

      const body = res.body;
      if (res.status === 403 || body?.message === 'UNAUTHORIZED') {
        setSaveErrorText('Unauthorized: You must be an administrator of this organization to edit it.');
        return;
      }

      if (res.status !== 200 || body?.message !== 'SUCCESS') {
        setSaveErrorText(body?.detail || 'Failed to save changes. Please try again.');
        return;
      }

      // Navigate back to organization dashboard
      navigate(`/o/${encodeURIComponent(org_path_name)}`);
    } catch {
      setSaveErrorText('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading && !org && !errorText) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', height: '100vh' }}>
        <GradientCircularProgress />
      </Box>
    );
  }

  if (errorText) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', px: 2 }}>
        <Typography color="error">{errorText}</Typography>
      </Box>
    );
  }

  if (!org || !org_path_name) {
    return <></>;
  }

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'transparent',
          padding: '0px 40px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 4,
          flexWrap: 'wrap',
          mt: 0,
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Edit Organization Details
          </Typography>
        </Box>
      </Box>

      {saveErrorText && (
        <Box sx={{ px: '40px', mb: 4 }}>
          <Alert severity="error" onClose={() => setSaveErrorText(null)}>
            {saveErrorText}
          </Alert>
        </Box>
      )}

      <Box
        sx={{ p: 2 }}
        style={{
          backgroundColor: theme.layout.defaults.backgroundColor,
          borderRadius: '16px',
          padding: '40px',
          width: '100%',
        }}
      >
        <form onSubmit={handleSave}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: '600px' }}>
            <TextField
              label="Organization Name"
              variant="outlined"
              fullWidth
              required
              value={name}
              disabled={isSaving}
              InputProps={{
                readOnly: true,
              }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Description"
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isSaving}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Google Calendar ID"
              variant="outlined"
              fullWidth
              value={googleCalendarId}
              onChange={(e) => setGoogleCalendarId(e.target.value)}
              disabled={isSaving}
              placeholder="e.g. your-org-calendar-id@group.calendar.google.com"
              helperText="The Google Calendar ID used to synchronize upcoming job events and process calendar updates."
              InputLabelProps={{ shrink: true }}
            />

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <PrimaryBlueButton
                buttonText={isSaving ? 'Saving...' : 'Save Changes'}
                additionalProps={{ disabled: isSaving, type: 'submit', style: { height: '48px', padding: '0 32px' } }}
              />
              <BorderedButton
                onClick={() => navigate(`/o/${encodeURIComponent(org_path_name)}`)}
                buttonText="Cancel"
                additionalProps={{ disabled: isSaving, style: { height: '48px', padding: '0 32px' } }}
              />
            </Box>
          </Box>
        </form>
      </Box>
    </>
  );
}
